import { classNames } from '@commenda/utils';
import React from 'react';
import { Skeleton } from '../../Skeleton';
import { TableCell as TCell, TableRow as TRow } from '../../Table';
import { DEFAULT_COLUMN_WIDTH } from '../constants';
import { ColumnGroup, RowHeightView } from '../types';
import {
  FLEX_SPACER_BODY_CLASSNAME,
  FLEX_SPACER_STYLE,
  getColumnId,
  getColumnWidthStyle,
} from '../utils';

interface TableLoadingStateProps<TData, TValue> {
  groups: ColumnGroup<TData, TValue>[];
  rowHeightView: RowHeightView;
  tableCellClassName?: string;
  tableRowClassName?: string;
  loadingRowsCount?: number;
  showPagination?: boolean;
  columnResizeConfig?: { isEnabled: boolean };
}

export function TableLoadingState<TData, TValue>({
  groups,
  rowHeightView,
  tableCellClassName = '',
  tableRowClassName = '',
  loadingRowsCount = 10,
  showPagination = true,
  columnResizeConfig,
}: TableLoadingStateProps<TData, TValue>) {
  const flattenedColumns = groups.flatMap((group) => group.columns);
  const rowHeight = rowHeightView === 'expanded' ? 'h-[52px]' : 'h-11';
  // Match the loaded table's spacer and lock widths to the --col vars (no jump).
  const showSpacer = !!columnResizeConfig?.isEnabled;
  const rightStickyGroup = groups.find((g) => g.sticky === 'right');
  const firstRightStickyColId = rightStickyGroup?.columns[0]
    ? getColumnId(rightStickyGroup.columns[0])
    : undefined;

  return (
    <>
      {/* Loading skeleton rows */}
      {Array.from({ length: loadingRowsCount }, (_, rowIndex) => (
        <TRow
          key={`loading-row-${rowIndex}`}
          className={classNames(
            'border-b border-border-primary last:border-b-0',
            rowHeight,
            tableRowClassName
          )}
        >
          {flattenedColumns.map((column, columnIndex) => {
            const columnId = getColumnId(column);
            const isLastColumn = columnIndex === flattenedColumns.length - 1;

            const cell = (
              <TCell
                key={`loading-cell-${rowIndex}-${columnId}`}
                className={classNames(
                  'px-4',
                  '',
                  rowHeight,
                  tableCellClassName
                )}
                style={
                  showSpacer
                    ? getColumnWidthStyle(
                        column.width,
                        true,
                        column.width ?? DEFAULT_COLUMN_WIDTH,
                        columnId,
                        true
                      )
                    : {
                        width: column.width ? `${column.width}px` : 'auto',
                        minWidth: column.width ? `${column.width}px` : '100px',
                      }
                }
              >
                <div className="flex items-center gap-2">
                  {/* First column checkbox skeleton */}
                  {columnIndex === 0 && <Skeleton className="w-4 h-4" />}

                  {/* Main content skeleton */}
                  <div className="flex-1">
                    <Skeleton
                      className={classNames(
                        rowHeightView === 'expanded' ? 'h-4' : 'h-3'
                      )}
                      style={{
                        width: `${Math.random() * 40 + 60}%`, // Random width between 60-100%
                      }}
                    />
                    {/* Additional skeleton lines for expanded view */}
                    {rowHeightView === 'expanded' && Math.random() > 0.5 && (
                      <Skeleton
                        className="h-3 mt-2"
                        style={{
                          width: `${Math.random() * 30 + 40}%`, // Random width between 40-70%
                        }}
                      />
                    )}
                  </div>
                </div>
              </TCell>
            );
            return (
              <React.Fragment
                key={`loading-cell-wrap-${rowIndex}-${columnId}`}
              >
                {showSpacer && columnId === firstRightStickyColId && (
                  <TCell
                    aria-hidden
                    className={FLEX_SPACER_BODY_CLASSNAME}
                    style={FLEX_SPACER_STYLE}
                  />
                )}
                {cell}
              </React.Fragment>
            );
          })}
          {showSpacer && !rightStickyGroup && (
            <TCell
              aria-hidden
              className={FLEX_SPACER_BODY_CLASSNAME}
              style={FLEX_SPACER_STYLE}
            />
          )}
        </TRow>
      ))}

      {/* Pagination skeleton - only show if showPagination is true */}
      {showPagination && (
        <TRow>
          <TCell
            colSpan={flattenedColumns.length + (showSpacer ? 1 : 0)}
            className="border-t border-border-primary p-4"
          >
            <div className="flex items-center justify-between">
              {/* Left side pagination controls */}
              <div className="flex items-center gap-2">
                <Skeleton className="w-8 h-8" />
                <div className="flex items-center gap-1">
                  <Skeleton className="w-6 h-6" />
                  <Skeleton className="w-6 h-6" />
                  <Skeleton className="w-6 h-6" />
                  <Skeleton className="w-4 h-4" />
                  <Skeleton className="w-6 h-6" />
                  <Skeleton className="w-6 h-6" />
                  <Skeleton className="w-6 h-6" />
                </div>
                <Skeleton className="w-8 h-8" />
              </div>

              {/* Center jump to page */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Jump to page:</span>
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-8 h-6" />
              </div>

              {/* Right side rows per page */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">No of rows</span>
                <Skeleton className="w-12 h-8" />
                <Skeleton className="w-4 h-4" />
              </div>
            </div>
          </TCell>
        </TRow>
      )}
    </>
  );
}
